import React, { useState, useEffect, useRef } from "react";
import { Spin, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { getResearchByConversation } from "../../../services/polly/researchService";

const STATUS_COLORS = {
  pending:     "processing",
  researched:  "purple",
  validated:   "success",
  delivered:   "cyan",
  failed:      "error",
};

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Larger component for conversation detail / drawer view.
 * Fetches the research request linked to a conversation and displays
 * status, question, last updated, and a link to the full Research Queue.
 *
 * Shows nothing if no linked research request exists.
 *
 * Props:
 *   conversationId - string/number, required
 *   customerUuid   - string (currently unused, reserved for future direct lookup)
 */
export default function ResearchStatusIndicator({ conversationId, customerUuid }) {
  const [research, setResearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!conversationId) {
      setResearch(null);
      setLoaded(true);
      return;
    }
    setLoading(true);
    setLoaded(false);
    getResearchByConversation(conversationId)
      .then((res) => {
        if (mountedRef.current) setResearch(res);
      })
      .catch((err) => {
        console.warn("Failed to fetch research for conversation:", err);
        if (mountedRef.current) setResearch(null);
      })
      .finally(() => {
        if (mountedRef.current) {
          setLoading(false);
          setLoaded(true);
        }
      });
  }, [conversationId]);

  // Show nothing if still loading or no linked research
  if (loading) {
    return (
      <div style={{ padding: "8px 0" }}>
        <Spin size="small" />
      </div>
    );
  }
  if (!loaded || !research) return null;

  const statusColor = STATUS_COLORS[research.status] || "default";
  const updatedAt = research.updatedAt || research.createdAt;

  return (
    <div className="conversation-detail-section">
      <h4>
        <SearchOutlined style={{ marginRight: 6 }} />
        Research Request
      </h4>
      <div className="conversation-detail-row">
        <span className="conversation-detail-label">Status</span>
        <span className="conversation-detail-value">
          <Tag color={statusColor} style={{ fontSize: 11 }}>
            {research.status || "unknown"}
          </Tag>
        </span>
      </div>
      {research.question && (
        <div className="conversation-detail-row">
          <span className="conversation-detail-label">Question</span>
          <span className="conversation-detail-value" style={{ fontSize: 12 }}>
            {research.question}
          </span>
        </div>
      )}
      <div className="conversation-detail-row">
        <span className="conversation-detail-label">Last updated</span>
        <span className="conversation-detail-value">
          {formatDate(updatedAt)}
        </span>
      </div>
      <div style={{ marginTop: 6 }}>
        <a
          href="/polly/research"
          onClick={(e) => {
            e.preventDefault();
            window.location.href = "/polly/research";
          }}
          style={{ fontSize: 12 }}
        >
          View in Research Queue &rsaquo;
        </a>
      </div>
    </div>
  );
}
